import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { activateActivity, listAllActivities, resetAllActivities } from "../../store/actions";
import { DataGrid, GridFooterContainer, GridOverlay } from "@mui/x-data-grid";
import { useSelector, useDispatch } from "react-redux";
import { Add, ArrowForward, LocalActivityOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { tableStyle } from "../../components";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import AvailabilityUpdation from "../../components/AvailabilityUpdation";
import ActivityFormModal from "./ActivityFormModal";

const ActivitiesList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const { isLoading, activitiesList, showViewMore } = useSelector(
    ({
      loading,
      activities: {
        activities: { list, showViewMore },
      },
    }) => ({
      isLoading: loading,
      activitiesList: list,
      showViewMore,
    })
  );

  useEffect(() => {
    document.title = 'Activities';
    dispatch(resetAllActivities());
    fetchMoreData();
    // eslint-disable-next-line
  }, [dispatch]);

  const fetchMoreData = () => {
    dispatch(listAllActivities());
  }

  const CustomFooter = () => (
    <GridFooterContainer>
      <Stack width="100%" alignItems="center">
        {showViewMore ? (
          <Button fullWidth onClick={fetchMoreData}>
            <b>view more</b>
          </Button>
        ) : (
          <Typography variant="h6" color={colors.grey[300]}>
            All activities listed
          </Typography>
        )}
      </Stack>
    </GridFooterContainer>
  );

  const NoActivitiesOverlay = () => (
    <GridOverlay>
      <Stack alignItems="center" spacing={1} sx={{ p: 4 }}>
        <LocalActivityOutlined sx={{ fontSize: 44, color: colors.grey[500] }} />
        <Typography fontWeight={700} color={colors.grey[200]}>
          No activities yet
        </Typography>
        <Typography variant="body2" color={colors.grey[400]} textAlign="center">
          Add your first experience to make it bookable by guests.
        </Typography>
      </Stack>
    </GridOverlay>
  );

  const columns = [
    { field: "name", headerName: "Name", width: 240, cellClassName: "name-column--cell" },
    {
      field: "rate",
      headerName: "Rate / person",
      width: 140,
      valueGetter: ({ row }) => `₹ ${Number(row?.rate || 0).toFixed(2)}`,
    },
    {
      field: "pax",
      headerName: "Pax",
      width: 100,
      sortable: false,
      renderCell: ({ row: { minimum_pax, maximum_pax } }) => {
        const min = minimum_pax ?? 1;
        return (
          <Typography variant="body2">
            {maximum_pax ? `${min}–${maximum_pax}` : `${min}+`}
          </Typography>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row: { description } }) => (
        <Tooltip title={description || ""}>
          <Typography variant="body2" color={colors.grey[300]} noWrap>
            {description || "—"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "is_active",
      headerName: "Availability",
      width: 120,
      renderCell: ({ row: { is_active, _id, name } }) => (
        <AvailabilityUpdation
          name={name}
          isActive={is_active}
          action={() => dispatch(activateActivity({ id: _id }))}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Details",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="contained"
          endIcon={<ArrowForward fontSize="small" />}
          onClick={() => navigate(`/activity-details/${row._id}`)}
          sx={{ fontWeight: 700 }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px" mb={15}>
      <Stack
        direction={{ sm: "row", xs: "column" }}
        justifyContent={{ sm: "space-between", xs: "start" }}
        alignItems={{ sm: "center", xs: "start" }}
        m="20px 0"
      >
        <Header
          title="ACTIVITIES"
          subtitle="Experiences and add-ons guests can book alongside their stay"
        />

        <Box>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
            onClick={() => setShowModal(true)}
          >
            <Add sx={{ mr: "10px" }} />
            Add new activity
          </Button>
        </Box>
      </Stack>
      <Box
        m="8px 0 0 0"
        height="68vh"
        sx={tableStyle(colors)}
      >
        <DataGrid
          rows={activitiesList || []}
          columns={columns}
          getRowId={(row) => row?._id}
          loading={isLoading}
          disableSelectionOnClick
          components={{ Footer: CustomFooter, NoRowsOverlay: NoActivitiesOverlay }}
        />
      </Box>

      <ActivityFormModal
        open={showModal}
        onClose={() => setShowModal(false)}
      />

    </Box>
  );
};

export default ActivitiesList;
